import {
  findNode,
  FormFieldDefinition,
  FormGroupDefinition,
  FormModel,
  FormNode,
  GroupStyle,
  StringFieldDefinition,
} from '@msdk/definitions'

/**
 * The function `formTraverse` traverses a form structure in a depth-first manner.
 * It applies a callback function to each node in the form, passing the node and the current number of columns
 * as arguments.
 *
 * The traversal maintains the current number of columns for nested groups and resets it appropriately when
 * moving back up the tree.
 *
 * @param {FormNode | undefined} node - The `node` parameter represents the current node in the form structure.
 * @param {function} callback - The `callback` parameter is a function to apply to each node. It takes the node
 * and the current number of columns as arguments.
 */
function formTraverse(node: FormNode | undefined, callback: (node: FormNode, columns: number) => void) {
  if (!node) {
    return
  }

  const stack: { node: FormNode; columns: number }[] = [{ node, columns: -1 }]

  while (stack.length > 0) {
    const { node: currentNode, columns: currentColumns } = stack.pop()!

    callback(currentNode, currentColumns)

    let child = currentNode.firstChild
    const newColumns =
      currentNode.id !== 'ROOT' && currentNode.value.type === 'Group' && currentNode.value.formDefinition
        ? (currentNode.value.formDefinition as FormGroupDefinition).columns ?? currentColumns
        : currentColumns

    while (child) {
      stack.push({ node: child, columns: newColumns })
      child = child.nextSibling
    }
  }
}

/**
 * The function `findCenterGroupLeftSpacing` calculates the left spacing for a center group within a
 * form layout.
 */
export function findCenterGroupLeftSpacing(formModel: FormModel): number {
  let spacing = 0
  let centerGroupFound = false
  formModel.traverse((i) => {
    if (i.type === 'Group' && !centerGroupFound) {
      const groupDef = i as FormGroupDefinition
      // find if this is the wrapper group of the center group
      const centerGroup = groupDef?.layout?.find((g) => (g as any).groupStyle === GroupStyle.CenterGroup)
      if (centerGroup) {
        // this group contains a center group calculate the position on creen,
        // 100 : container.columns = x : centergroup.columnSpan
        const centerGroupColSpan = centerGroup.columnSpan === -1 ? 2 : centerGroup.columnSpan
        const centerGroupPosition = (100 * centerGroupColSpan) / groupDef.columns
        // we found the center position, now we determine the left border
        spacing = centerGroupPosition / 2
        centerGroupFound = true
      }
    }
  })
  return spacing
}

/**
 * The function `findMaxGroupWidth` calculates the maximum width of the groups in a form.
 * The width is returned as a percentage.
 *
 * This value can be used in a style definition like this: `style={{ width: '${maxWidth}%' }}`.
 * @param {FormNode} node - The `node` parameter is an object that represents the form structure and layout
 * to traverse.
 * @returns The maximum width of the groups in the form .
 */
export function findMaxGroupWidth(node: FormNode): number {
  let maxWidth = 0
  formTraverse(node, calculateMaxWidth)

  function calculateMaxWidth(currentNode: FormNode, currentColumns: number) {
    if (currentNode.id !== 'ROOT' && currentNode.value.type === 'Group') {
      const groupDef = currentNode.value.formDefinition as FormGroupDefinition
      if (groupDef) {
        const layoutItems = Array.isArray(groupDef.layout) ? groupDef.layout : []

        ;[...layoutItems, groupDef].forEach((item) => {
          const width = calculateItemWidth(item, groupDef, currentColumns)
          if (width > maxWidth) {
            maxWidth = width
          }
        })
      }
    }
  }

  function calculateItemWidth(item: any, groupDef: FormGroupDefinition, currentColumns: number) {
    const columnSpan = getColumnSpan(item, groupDef)
    const columns = Math.max(item.columns ?? 0, groupDef.columns ?? 0, currentColumns)
    const effectiveColumns = columns === -1 ? 2 : columns
    const width = (100 / effectiveColumns) * columnSpan
    return width
  }

  function getColumnSpan(item: any, groupDef: FormGroupDefinition): number {
    if (item.columnSpan && parseInt(item.columnSpan) > 0) {
      return parseInt(item.columnSpan)
    }
    /*
     * Special case for string fields with IsComment="true"
     * these fields must occupy 100% of the group width
     */
    if (item.type === 'Field') {
      const formFieldDef = item as FormFieldDefinition
      const fieldNode = findNode(node, formFieldDef.name)
      if (fieldNode?.value.fieldDefinition?.fieldDefinitionType === 'String') {
        const stringDef = fieldNode.value.fieldDefinition as StringFieldDefinition
        if (stringDef.isComment) {
          return groupDef.columns === -1 ? 2 : groupDef.columns
        }
      }
    }

    return -1
  }

  // set a default value of 100 when the calculated value would break the layout
  if (maxWidth === 0 || maxWidth > 100) {
    maxWidth = 100
  }

  return maxWidth
}
